import { Component, Input, ViewChild } from '@angular/core'
import { NgForm } from '@angular/forms'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { TranslateService } from '@ngx-translate/core'
import { APIService } from 'api/module'
import { MessageBoxService } from 'common/util/module'
import { NotifyService } from 'common/util/services/notify.service'
import { CustomField, MediaFile, MediaFileBundle, MediaRootDetail, ParametersMediaDefaultCustomFieldTypeEnum } from 'elements-sdk'
import { ParametersService } from 'main/services/parameters.service'
import { CustomFieldsService } from 'media.customFields'
import { MediaScannerService } from 'media/services/mediaScanner.service'
import { isBundle } from 'media/helpers'

interface FieldObject {
  name: string
  definition: CustomField
  multipleValues: boolean
  value: any
  readOnly: boolean
  originalValue: any
  erased: boolean
  unchanged: boolean
}

export type Item = MediaFile | MediaFileBundle

@Component({
  templateUrl: './customFieldsEditorModal.component.pug',
  styleUrls: ['./customFieldsEditorModal.component.scss'],
})
export class CustomFieldsEditorModalComponent {
  @Input() root: MediaRootDetail
  @Input() bundles: MediaFileBundle[] = []
  @Input() dirs: MediaFile[] = []

  parentLoaded = false
  parents: Record<number, MediaFile | undefined> = {}
  modeFields: Record<string, FieldObject[]> = {}
  fields: FieldObject[]
  mode: string = this.parameters.values.mediaDefaultCustomFieldType ?? ParametersMediaDefaultCustomFieldTypeEnum.File

  @ViewChild('form', { 'static': false }) form: NgForm

  constructor (
    private modalInstance: NgbActiveModal,
    private translate: TranslateService,
    private mediaScanner: MediaScannerService,
    private customFields: CustomFieldsService,
    private api: APIService,
    private messageBox: MessageBoxService,
    private parameters: ParametersService,
    private notify: NotifyService,
  ) { }

  setMode (mode) {
    this.mode = mode
    this.fields = this.modeFields[this.mode]
  }

  ngOnInit () {
    if (!this.bundles.length) {
      this.setMode(ParametersMediaDefaultCustomFieldTypeEnum.File)
    }

    const parents: Record<number, Promise<MediaFile>> = {}
    for (const obj of this.bundles) {
      const parentId = obj.mainfile.parent

      if (!parentId) {
        continue
      }

      if (!(parentId in parents)) {
        parents[parentId] = this.api.mediaLibrary.getMediaFile({
          id: parentId,
          includeEffectiveCustomFields: true,
        })
      }

      parents[parentId].then(parent => {
        this.parents[parentId] = parent
        return parent
      }).catch(() => null)
    }

    Promise.all(Object.values(parents)).then(() => {
      this.rebuild('parent')
      this.parentLoaded = true
    })

    for (const mode of ['file', 'asset']) {
      this.rebuild(mode)
    }

    this.fields = this.modeFields[this.mode]
  }

  rebuild (mode) {
    const fields = this.root.customFields!.map(field => {
      const values = new Set()

      const objs: Item[] = mode !== 'file' ? this.bundles : [...this.dirs, ...this.bundles]
      for (const obj of objs) {
        values.add(this.getValue(obj, field as any, mode))
      }

      const value = values.size === 1 ? [...values][0] : null

      return {
        name: field.name!,
        definition: field as any,
        multipleValues: values.size > 1,
        value,
        readOnly: mode === 'parent',
        originalValue: value,
        unchanged: true,
        erased: false,
      }
    })

    this.modeFields[mode] = fields
  }

  setValue (field: FieldObject, value) {
    if (field.value !== value) {
      field.value = value
      field.unchanged = false
    }
  }

  getValue (obj: Item, field: CustomField, mode) {
    if (isBundle(obj)) {
      if (mode === 'file') {
        return obj.mainfile.customFields?.[field.name] ?? null
      } else if (mode === 'parent') {
        const fields = (obj.mainfile.parentFile ?? this.parents[obj.mainfile.parent ?? 0])?.effectiveCustomFields
        return fields?.[field.name] ?? null
      } else {
        return obj.asset ? obj.asset.customFields[field.name] : null
      }
    }
    return obj.customFields?.[field.name] ?? null
  }

  async eraseField (field: FieldObject) {
    field.erased = !field.erased
  }

  async clearField (field: FieldObject) {
    field.erased = false
    field.value = field.originalValue
    setTimeout(() => {
      field.unchanged = true
    })
  }

  cancel () {
    this.modalInstance.dismiss()
  }

  save = async () => {
    const fields = {}
    const promises: Promise<any>[] = []

    for (const field of this.fields) {
      if (field.unchanged && !field.erased) {
        continue
      }
      this.customFields.registerAutocompletion(field.definition, field.value)
      fields[field.name] = field.erased ? null : field.value
    }

    await Promise.all(this.dirs.map(dir => {
      Object.assign(dir.customFields as any, fields)
      return this.api.mediaLibrary.patchMediaFile({
        id: dir.id,
        mediaFilePartialUpdate: {
          customFields: dir.customFields,
        },
      })
    }))

    if (this.dirs.length > 0) {
      this.messageBox.show({
        text: this.translate.instant('Changes in the directories won\'t become visible when searching until the search index is refreshed. Refresh it now?'),
        positive: this.translate.instant('Now'),
        negative: this.translate.instant('Later'),
      }).then(async () => {
        await Promise.all(this.dirs.map(dir =>
          this.mediaScanner.searchReindex(dir),
        ))
        this.notify.info(this.translate.instant('Recursive reindexing started'))
      }).catch(() => null)
    }

    for (const bundle of this.bundles) {
      if (this.mode === 'asset' && bundle.asset) {
        Object.assign(bundle.asset.customFields, fields)
        promises.push((async () => {
          const asset = await this.api.mediaLibrary.getAsset({ id: bundle.asset!.id })
          await this.api.mediaLibrary.patchAsset({
            id: asset.id,
            root: this.root.id,
            assetPartialUpdate: {
              customFields: { ...asset.customFields, ...bundle.asset!.customFields },
            },
          })
        })())
      } else {
        Object.assign(bundle.mainfile.customFields as any, fields)
        promises.push((async () => {
          const file = await this.api.mediaLibrary.getMediaFile({ id: bundle.mainfile.id })
          await this.api.mediaLibrary.patchMediaFile({
            id: file.id,
            root: this.root.id,
            mediaFilePartialUpdate: {
              customFields: { ...file.customFields, ...bundle.mainfile.customFields },
            },
          })
        })())
      }
    }

    await Promise.all(promises)
    this.modalInstance.close()
  }
}
